import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, DoorOpen, Rows3 } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar, PageHeader, Spinner, Empty } from "@/components/Common";
import { ContestCard } from "@/pages/Daily";
import { MessageButton } from "@/pages/Messaggi";
import { PathCard } from "@/pages/Percorsi";
import { RoomList } from "@/pages/Stanze";
import { TZ } from "@/pages/Oggi";

function Section({ title, hint, children, right }: any) {
  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div><h2 className="text-lg font-semibold">{title}</h2>{hint && <p className="text-sm text-ink/66">{hint}</p>}</div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Together() {
  const { data, isLoading } = useQuery({ queryKey: ["paths"], queryFn: () => api.get("/paths", { params: { tz: TZ } }).then((r) => r.data) });
  const mine = (data || []).filter((p: any) => p.me && !p.me.finished);
  const list = [...mine, ...(data || []).filter((p: any) => !mine.includes(p))].slice(0, 4);
  return (
    <Section title="Facciamolo insieme" hint="Percorsi di più giorni: ogni giorno una tappa da condividere. Senza vincitori."
      right={<Link to="/percorsi" className="text-sm text-ink/70 inline-flex items-center gap-1 min-h-[44px]" data-testid="cresciamo-all-paths">Tutti<ArrowRight size={14} /></Link>}>
      {isLoading ? <Spinner /> : !list.length ? <Empty testId="cresciamo-paths-empty">Nessun percorso disponibile.</Empty> : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="cresciamo-paths">{list.map((p: any) => <PathCard key={p.id} p={p} />)}</div>
      )}
    </Section>
  );
}

function Rooms() {
  return (
    <Section title="Stanze della community" hint="Chat aperte a tutti, divise per argomento."
      right={<Link to="/stanze" className="text-sm text-ink/70 inline-flex items-center gap-1 min-h-[44px]" data-testid="cresciamo-all-rooms">Tutte<ArrowRight size={14} /></Link>}>
      <RoomList limit={3} />
    </Section>
  );
}

function Challenges() {
  const { data, isLoading } = useQuery({ queryKey: ["daily"], queryFn: () => api.get("/daily").then((r) => r.data) });
  return (
    <Section title="Sfide condivise" hint="Partecipa con un tuo contenuto: conta esserci, non vincere."
      right={<Link to="/daily" className="text-sm text-ink/70 inline-flex items-center gap-1 min-h-[44px]" data-testid="cresciamo-all-challenges">Tutte<ArrowRight size={14} /></Link>}>
      {isLoading ? <Spinner /> : !data?.open?.length ? <Empty testId="challenges-empty">Nessuna sfida aperta in questo momento.</Empty> : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="cresciamo-challenges">{data.open.slice(0, 4).map((c: any, i: number) => <ContestCard key={c.id} c={c} i={i} compact />)}</div>
      )}
    </Section>
  );
}

function Connections() {
  const { data, isLoading } = useQuery({ queryKey: ["connections"], queryFn: () => api.get("/connections").then((r) => r.data) });
  return (
    <Section title="Le tue connessioni" hint="Persone con cui hai scelto di camminare insieme.">
      {isLoading ? <Spinner /> : !data?.length ? (
        <Empty testId="connections-empty">Ancora nessuna connessione. Apri il profilo di una persona e usa Bussa per proporle di connettervi.</Empty>
      ) : (
        <div className="surface p-1" data-testid="connections-list">
          {data.map((u: any) => (
            <div key={u.id} className="row" data-testid="connection-item">
              <Link to={`/u/${u.username}`} className="flex items-center gap-3 flex-1 min-w-0"><Avatar user={u} size={40} /><span className="min-w-0"><span className="font-semibold block truncate">{u.display_name}</span><span className="text-xs text-ink/60">@{u.username}</span></span></Link>
              <MessageButton userId={u.id} />
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

export default function Cresciamo() {
  return (
    <div data-testid="cresciamo-page">
      <PageHeader kicker="Crescita condivisa" title="Cresciamo" />
      <div className="px-5 md:px-10 max-w-3xl space-y-10 pb-12">
        <p className="text-ink/70 leading-relaxed -mt-2">Qui si cresce insieme: sfide piccole, persone vicine, progressi da celebrare. Nessuna classifica di popolarità.</p>
        <Together />
        <Rooms />
        <Challenges />
        <Connections />
        <div className="grid sm:grid-cols-2 gap-3">
          <Link to="/feed" className="surface p-5 flex items-center gap-3 hover:bg-white/60" data-testid="cresciamo-feed-link"><Rows3 size={19} className="text-[hsl(var(--primary))]" /><span className="flex-1 font-semibold">Leggi storie di crescita</span><ArrowRight size={16} /></Link>
          <Link to="/attivita?tab=bussa" className="surface p-5 flex items-center gap-3 hover:bg-white/60" data-testid="cresciamo-bussa-link"><DoorOpen size={19} className="text-[hsl(var(--primary))]" /><span className="flex-1 font-semibold">Richieste di connessione</span><ArrowRight size={16} /></Link>
        </div>
      </div>
    </div>
  );
}
